/**
 * Collectible pickup, carry, and delivery logic.
 * Pure functions — no side effects, no rendering.
 */

import type { PlayerState, PlanetState, CollectibleNode, CollectibleType, CarryableObjectState, GameState, ModeConfig, Vec3 } from '../types';
import { COLLECT_RANGE, COLLECT_MOVEMENT_COST, DEFAULT_CARRY_CAPACITY } from '../constants';
import { arcDistance } from './gameState';
import { dcos, dsin } from './dmath';

/**
 * Check if a player can collect a collectible node.
 */
export function canCollect(
  player: PlayerState,
  collectible: CollectibleNode,
  planets: PlanetState[]
): boolean {
  if (!player.alive || player.airborne) return false;
  if (collectible.collected) return false;
  if (player.planetIndex !== collectible.planetIndex) return false;
  if (player.movementRemaining < COLLECT_MOVEMENT_COST) return false;

  const planet = planets[player.planetIndex];
  if (!planet) return false;

  const dist = arcDistance(player.surfaceDir, collectible.surfaceDir, planet.radius);
  return dist <= COLLECT_RANGE;
}

/**
 * Collect an item: mark collectible as collected, create a carryable object.
 */
export function collectItem(state: GameState, playerId: number, collectibleId: number): GameState {
  const collectible = state.collectibles.find(c => c.id === collectibleId);
  if (!collectible) return state;

  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  // Mark collectible as collected
  const updatedCollectibles = state.collectibles.map(c =>
    c.id === collectibleId ? { ...c, collected: true, collectedBy: playerId } : c
  );

  // Create a carryable from the collectible
  const nextCarryableId = state.carryables.length > 0
    ? Math.max(...state.carryables.map(c => c.id)) + 1
    : 0;

  const carryableType = collectible.type === 'dataFragment' ? 'dataFragment' as const
    : 'salvageNode' as const;

  const newCarryable: CarryableObjectState = {
    id: nextCarryableId,
    type: carryableType,
    teamIndex: -1,
    planetIndex: player.planetIndex,
    surfaceDir: { ...player.surfaceDir },
    carriedBy: playerId,
    originPlanetIndex: collectible.planetIndex,
    originSurfaceDir: { ...collectible.surfaceDir },
    alive: true,
    value: collectible.value,
    weight: collectible.weight,
    sourceCollectibleId: collectible.id,
    sourcePlanetIndex: collectible.planetIndex,
  };

  // Deduct movement
  const updatedPlayers = state.players.map(p =>
    p.id === playerId
      ? { ...p, movementRemaining: p.movementRemaining - COLLECT_MOVEMENT_COST }
      : p
  );

  return {
    ...state,
    collectibles: updatedCollectibles,
    carryables: [...state.carryables, newCarryable],
    players: updatedPlayers,
  };
}

/**
 * Get all carryables that originated from collectibles, carried by a player.
 */
export function getCarriedCollectibles(
  playerId: number,
  carryables: CarryableObjectState[]
): CarryableObjectState[] {
  return carryables.filter(
    c => c.carriedBy === playerId && c.sourceCollectibleId != null
  );
}

/**
 * Get total carry weight for a player.
 */
export function getCarryWeight(
  playerId: number,
  carryables: CarryableObjectState[]
): number {
  return getCarriedCollectibles(playerId, carryables)
    .reduce((sum, c) => sum + (c.weight ?? 1), 0);
}

/**
 * Check if a player can carry more items (under capacity).
 */
export function canCarryMore(
  playerId: number,
  carryables: CarryableObjectState[],
  modeConfig: ModeConfig
): boolean {
  const capacity = modeConfig.unitCarryCapacity ?? DEFAULT_CARRY_CAPACITY;
  return getCarryWeight(playerId, carryables) < capacity;
}

/**
 * Deliver an item — remove the carryable from player inventory.
 * Mode-specific scoring is handled by the caller.
 */
export function deliverItem(state: GameState, playerId: number, carryableId: number): GameState {
  const carryable = state.carryables.find(c => c.id === carryableId);
  if (!carryable || carryable.carriedBy !== playerId) return state;

  const updatedCarryables = state.carryables.filter(c => c.id !== carryableId);

  return {
    ...state,
    carryables: updatedCarryables,
  };
}

/**
 * Drop all collectible-sourced carryables on player death.
 */
export function dropCollectiblesOnDeath(state: GameState, playerId: number): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const updatedCarryables = state.carryables.map(c => {
    if (c.carriedBy !== playerId || c.sourceCollectibleId == null) return c;
    return {
      ...c,
      carriedBy: null,
      planetIndex: player.planetIndex,
      surfaceDir: { ...player.surfaceDir },
    };
  });

  return {
    ...state,
    carryables: updatedCarryables,
  };
}

/**
 * Generate collectible nodes on planet surfaces.
 */
export function generateCollectibleNodes(
  planets: PlanetState[],
  type: CollectibleType,
  count: number,
  rng: { range: (min: number, max: number) => number; int: (min: number, max: number) => number },
  valueRange: [number, number],
  weightRange: [number, number]
): CollectibleNode[] {
  const nodes: CollectibleNode[] = [];
  const spawnablePlanets = planets
    .map((p, i) => ({ planet: p, index: i }))
    .filter(e => e.planet.type === 'planet');

  if (spawnablePlanets.length === 0) return nodes;

  for (let i = 0; i < count; i++) {
    const { index: planetIndex } = spawnablePlanets[i % spawnablePlanets.length];

    const theta = rng.range(0, Math.PI * 2);
    const phi = rng.range(0.2, Math.PI - 0.2);
    const surfaceDir: Vec3 = {
      x: dsin(phi) * dcos(theta),
      y: dcos(phi),
      z: dsin(phi) * dsin(theta),
    };

    nodes.push({
      id: i,
      type,
      planetIndex,
      surfaceDir,
      value: rng.int(valueRange[0], valueRange[1]),
      weight: rng.int(weightRange[0], weightRange[1]),
      collected: false,
      collectedBy: null,
    });
  }

  return nodes;
}
